import React from "react";
import ReactSwitch from "react-switch";
import { components as ReactSelectComponents } from "react-select";
import {
    CrossIcon,
    StyledLabel,
    StyledSelectInput,
    StyledTextInput,
} from "@aureskonnect/react-ui";

import {
    AvForm,
    AvRadioGroup,
    AvRadio,
    AvGroup,
} from "availity-reactstrap-validation";

import classnames from "classnames";

import {
    CustomCardBody,
    CustomCardFooter,
    CustomCardHeader,
} from "@components/Common/CustomCardStyled";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";

import PrintersPathsModal from "./PrintersPathsModal";
import {
    ipMatchWord,
    numberMatchWord,
    numberMatchWordWithNullInLeft,
} from "../../../constants";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";

import "./index.css";
import { usePrinterConfig } from "./usePrinterConfig";
import { isSeriesPortFilled } from "./printerPortCheck";
import { canSavePrinter } from "./printerSaveGate";

// StyledSelectInput is styled(Select) from react-select v3: the `id` prop lands
// on the container, not the focusable input. We expose these ids via `inputId`
// (so the <label htmlFor> targets the real input) and a custom Input component
// (so aria-invalid / aria-describedby reach the input, not the wrapper).
const PORT_NUM_INPUT_ID = "port_num";
const PORT_NUM_ERROR_ID = "port_num_error";

type PrintersType = {
    setIsAddPrinterClicked: Function;
    setDataIsUpdated: Function;
    isAddPrinterClicked: boolean;
    editedData: any;
    setEditedData: Function;
    isAddNewPrinterButtonClicked: boolean;
    shopIdPrinter: string;
    localShopId?: any;
    inputDesignation: boolean;
    setInputDesignation: Function;
    inputIp: boolean;
    setInputIp: Function;
    dataIsUpdated?: boolean | number;
    dataTable?: any;
    setIsUpdateOperation: Function;
    isConsult: boolean;
    setIsConsult: Function;
    isComingFromAssociate?: boolean;
};

export default function PrintersConfig(props: PrintersType) {
    const {
        setIsAddPrinterClicked,
        isAddPrinterClicked,
        setDataIsUpdated,
        editedData,
        setEditedData,
        inputDesignation,
        setInputDesignation,
        inputIp,
        setInputIp,
        isConsult,
        setIsConsult,
    } = props;

    const {
        t,
        printerType,
        setPrinterType,
        designation,
        setDesignation,
        mark,
        setMark,
        remark,
        setRemark,
        speed,
        setSpeed,
        ip,
        setIp,
        fontSize,
        setFontSize,
        ticketWidth,
        setTicketWidth,
        ipPortNumber,
        setIpPortNumber,
        printerCode,
        setPrinterCode,
        portSeries,
        setPortSeries,
        openDrawer,
        setOpenDrawer,
        tickets,
        setTickets,
        ticketsList,
        inputTicket,
        setInputTicket,
        inputPortSeries,
        setInputPortSeries,
        isWaiting,
        isPrintersPathsModalOpened,
        setIsPrintersPathsModalOpened,
        exist,
        emptyIp,
        setEmptyIp,
        isModalOpened,
        setIsModalOpened,
        codeIsExist,
        isCodeValid,
        setIsCodeValid,
        printerPath,
        setPrinterPath,
        isUsbPrinter,
        isIpPrinter,
        pathname,
        fontSizeArray,
        ticketWidthArray,
        portNumArray,
        printerSpeedArray,
        isActionsClicked,
        isEdited,
        actions,
        validationAction,
        uniqueDesignationIp,
        activateCheckbox,
        handleEditButtonClickEvent,
        handleValidateButtonClickEvent,
        setIsActionsClicked,
        setActions,
        setIsEdited,
        resetAllPeripherals,
        clearMultiCustomSelectionData,
    } = usePrinterConfig(props);

    // react-select v3 only forwards a fixed aria set to its inner input, so we
    // override the Input component to carry the invalid state and the link to
    // the error message. Memoized on inputPortSeries to avoid remounting the
    // input (which would drop focus) on unrelated re-renders.
    const portNumSelectComponents = React.useMemo(
        () => ({
            Input: (inputProps: any) => (
                <ReactSelectComponents.Input
                    {...inputProps}
                    aria-invalid={inputPortSeries}
                    aria-describedby={
                        inputPortSeries ? PORT_NUM_ERROR_ID : undefined
                    }
                />
            ),
        }),
        [inputPortSeries]
    );

    return (
        <React.Fragment>
            <AvForm className="card">
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ maxHeight: "76px" }}
                >
                    <h5 className="pt-3 imp_txt_addPrinterDevice">
                        {isConsult === true
                            ? t("View peripheral: printer")
                            : editedData.length === 0
                            ? t("Add device: printer")
                            : t("Modify a device: printer")}
                    </h5>
                    <CrossIcon
                        style={{ cursor: "pointer" }}
                        className="imp_icn_closeForm"
                        height={13}
                        width={13}
                        onClick={() => {
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    activateCheckbox();
                                    setEditedData([]);
                                    setIsAddPrinterClicked!(
                                        !isAddPrinterClicked
                                    );
                                    setIsConsult(false);
                                    resetAllPeripherals();
                                    clearMultiCustomSelectionData();
                                    setIsAddPrinterClicked!(
                                        !isAddPrinterClicked
                                    );
                                    setIsEdited(false);
                                });
                            } else {
                                activateCheckbox();
                                setEditedData([]);
                                setIsAddPrinterClicked!(!isAddPrinterClicked);
                                setIsConsult(false);
                                setIsEdited(false);
                                resetAllPeripherals();
                                clearMultiCustomSelectionData();
                            }
                        }}
                    />
                </CustomCardHeader>
                <CustomCardBody
                    className="pl-3 pr-5"
                    style={{
                        maxHeight:
                            pathname === "/peripherals/printers"
                                ? "500px"
                                : "522px",
                        overflowY: "auto",
                    }}
                >
                    <StyledLabel>{t("Type of configuration")}</StyledLabel>
                    <AvRadioGroup
                        name="locationType"
                        required
                        inline
                        className={classnames("form-check-label__clz", {
                            readOnly__clz: isConsult,
                        })}
                        value={printerType}
                    >
                        <AvRadio
                            onChange={() => {
                                setPrinterType("ip");
                                setIsEdited(true);
                            }}
                            className="ml-4 imp_icn_ip"
                            label={<StyledLabel>{t("IP")}</StyledLabel>}
                            name="type"
                            value="ip"
                        />
                        <AvRadio
                            onChange={() => {
                                setPrinterType("series");
                                setIp("");
                                setIsEdited(true);
                            }}
                            id="series"
                            className="ml-4 mt-0 imp_icn_series"
                            label={<StyledLabel>{t("Series")}</StyledLabel>}
                            name="type"
                            value="series"
                        />
                        <AvRadio
                            className="ml-4 imp_icn_usb"
                            onChange={() => {
                                setPrinterType("usb");
                                setIp("");
                                setIsEdited(true);
                            }}
                            label={<StyledLabel>{t("USB")}</StyledLabel>}
                            name="type"
                            value="usb"
                            id="usb"
                        />
                    </AvRadioGroup>
                    <AvGroup style={{ width: "100%" }}>
                        <StyledLabel
                            htmlFor="name"
                            className="required__clz mt-3"
                        >
                            {t("Designation")}
                        </StyledLabel>

                        <StyledTextInput
                            autocomplete="off"
                            className={classnames("imp_inp_printerName", {
                                input__clz:
                                    inputDesignation ||
                                    exist.isNameExist ||
                                    (designation?.trim() === "" &&
                                        designation !== ""),
                                readOnly__clz: isConsult,
                            })}
                            id="designation"
                            name="designation"
                            placeholder={t("Write")}
                            type="text"
                            onChange={(e: any) => {
                                setDesignation(e.target.value);
                                setIsEdited(true);
                                setInputDesignation(false);
                                uniqueDesignationIp(
                                    e.target.value,
                                    "designation"
                                );
                            }}
                            value={designation}
                            autoFocus={isConsult === true ? false : true}
                        />
                        {inputDesignation ||
                        (designation.trim() === "" && designation !== "") ? (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("Please enter a designation")}
                            </div>
                        ) : null}
                        {exist.isNameExist ? (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("The name is already exists")!}
                            </div>
                        ) : null}
                    </AvGroup>
                    {printerType === "usb" ? (
                        <div className="justify-content-between d-flex mt-3">
                            <AvGroup style={{ width: "100%" }}>
                                <StyledLabel htmlFor="path" className=" ">
                                    {t("Name of printer")}
                                </StyledLabel>
                                <StyledTextInput
                                    className={classnames(
                                        "imp_inp_selectPrinter",
                                        {
                                            readOnly__clz: isConsult,
                                        }
                                    )}
                                    style={{ width: "100%" }}
                                    id="path"
                                    name="path"
                                    placeholder={t("Write")}
                                    type="text"
                                    value={printerPath}
                                    onChange={(e: any) => {
                                        setPrinterPath(e.target.value);
                                        setIsEdited(true);
                                    }}
                                />
                            </AvGroup>
                        </div>
                    ) : null}
                    <AvGroup
                        style={{
                            width: "100%",
                        }}
                    >
                        <StyledLabel
                            className="required__clz mt-3"
                            htmlFor="example-input"
                        >
                            {t("Printer code")}
                        </StyledLabel>
                        <StyledTextInput
                            className={classnames("imp_inp_code", {
                                readOnly__clz: isConsult,
                            })}
                            autocomplete="off"
                            id="code"
                            name="code"
                            placeholder={t("Write")}
                            onChange={(e: any) => {
                                setPrinterCode(e.target.value);
                                setIsEdited(true);
                                const isValid = e.target.value.match(
                                    numberMatchWordWithNullInLeft
                                );
                                setIsCodeValid(isValid !== null);
                            }}
                            value={printerCode}
                            validate={{
                                required: {
                                    value: true,
                                    errorMessage: `${t(
                                        "Please enter a printer code"
                                    )!}`,
                                },
                                pattern: {
                                    value: numberMatchWordWithNullInLeft,
                                    errorMessage: t("Printer code is invalid"),
                                },
                            }}
                            maxMenuHeight="90px"
                        />
                        {codeIsExist ? (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("The code is already exists")!}
                            </div>
                        ) : null}
                    </AvGroup>
                    {isIpPrinter ? (
                        <AvGroup>
                            <StyledLabel
                                htmlFor="example-input"
                                className="required__clz mt-3"
                            >
                                {t("IP address")}
                            </StyledLabel>
                            <StyledTextInput
                                autocomplete="off"
                                style={{ width: "100%" }}
                                className={classnames("imp_inp_ipAddress", {
                                    input__clz:
                                        inputIp || emptyIp || exist.isIpExist,
                                    readOnly__clz: isConsult,
                                })}
                                id="ip"
                                name="ip"
                                placeholder={t("Write")}
                                type="text"
                                onChange={(e: any) => {
                                    setIp(e.target.value);
                                    setIsEdited(true);
                                    if (
                                        !e.target.value.match(ipMatchWord) &&
                                        e.target.value !== ""
                                    ) {
                                        setInputIp(true);
                                    } else {
                                        setInputIp(false);
                                    }
                                    if (e.target.value !== "") {
                                        setEmptyIp(false);
                                    }
                                    uniqueDesignationIp(e.target.value, "ip");
                                }}
                                value={ip}
                            />

                            {emptyIp || inputIp || exist.isIpExist ? (
                                <div
                                    style={{
                                        width: "100%",
                                        marginTop: "0.25rem",
                                        fontSize: "80%",
                                        color: "#f46a6a",
                                    }}
                                >
                                    {t(
                                        emptyIp
                                            ? "Please enter a IP address"
                                            : inputIp
                                            ? "IP address is invalid"
                                            : exist.isIpExist
                                            ? "The ip address is already exists"
                                            : ""
                                    )}
                                </div>
                            ) : null}
                        </AvGroup>
                    ) : null}
                    {isIpPrinter || printerType === "series" ? (
                        <div
                            className="d-flex align-items-start"
                            style={{ gap: "10%" }}
                        >
                            {isIpPrinter ? (
                                <AvGroup
                                    style={{
                                        width: "100%",
                                    }}
                                >
                                    <StyledLabel
                                        className="mt-3"
                                        htmlFor="example-input"
                                    >
                                        {t("Port number")}
                                    </StyledLabel>
                                    <StyledTextInput
                                        className={classnames(
                                            "imp_inp_portNumber",
                                            {
                                                readOnly__clz: isConsult,
                                            }
                                        )}
                                        autocomplete="off"
                                        id="port"
                                        name="port"
                                        placeholder={t("Write")}
                                        onChange={(e: any) => {
                                            setIpPortNumber(e.target.value);
                                            setIsEdited(true);
                                        }}
                                        value={ipPortNumber}
                                        validate={{
                                            // Port mandatory only for series;
                                            // IP port is optional, format
                                            // checked only when filled.
                                            pattern: {
                                                value: numberMatchWord,
                                                errorMessage: t(
                                                    "Port number is invalid"
                                                ),
                                            },
                                        }}
                                        maxMenuHeight="90px"
                                    />
                                </AvGroup>
                            ) : (
                                <AvGroup style={{ width: "50%" }}>
                                    <StyledLabel
                                        className="required__clz mt-3"
                                        htmlFor={PORT_NUM_INPUT_ID}
                                    >
                                        {t("Port number")}
                                    </StyledLabel>
                                    <StyledSelectInput
                                        style={{ width: "100%" }}
                                        name="port_num"
                                        inputId={PORT_NUM_INPUT_ID}
                                        components={portNumSelectComponents}
                                        options={portNumArray}
                                        onChange={(e: any) => {
                                            setPortSeries(e.label);
                                            setInputPortSeries(false);
                                            setIsEdited(true);
                                        }}
                                        placeholder={t("Write")}
                                        value={{
                                            value: portSeries,
                                            label: portSeries,
                                        }}
                                        noOptionsMessage={() => t("No options")}
                                        maxMenuHeight="90px"
                                        className={classnames(
                                            "imp_inp_portNumber",
                                            {
                                                input__clz: inputPortSeries,
                                                readOnly__clz: isConsult,
                                            }
                                        )}
                                    />
                                    {inputPortSeries ? (
                                        <div
                                            id={PORT_NUM_ERROR_ID}
                                            role="alert"
                                            aria-live="assertive"
                                            data-testid="peripherals-printer-port-series-error"
                                            style={{
                                                width: "100%",
                                                marginTop: "0.25rem",
                                                fontSize: "80%",
                                                color: "#f46a6a",
                                            }}
                                        >
                                            {t("Please select a port number")}
                                        </div>
                                    ) : null}
                                </AvGroup>
                            )}

                            {printerType === "series" && (
                                <AvGroup style={{ width: "50%" }}>
                                    <StyledLabel
                                        className="mt-3"
                                        htmlFor="speed"
                                    >
                                        {t("Speed")}
                                    </StyledLabel>
                                    <StyledSelectInput
                                        value={{
                                            label: speed,
                                            value: speed,
                                        }}
                                        id="speed"
                                        name="speed"
                                        placeholder={t("Write")}
                                        options={printerSpeedArray}
                                        onChange={(e: any) => {
                                            setSpeed(e.label);
                                            setIsEdited(true);
                                        }}
                                        noOptionsMessage={() => t("No options")}
                                        maxMenuHeight="90px"
                                        className={classnames("imp_inp_speed", {
                                            readOnly__clz: isConsult,
                                        })}
                                    />
                                </AvGroup>
                            )}
                        </div>
                    ) : null}

                    <div
                        className="d-flex align-items-start"
                        style={{ gap: "10%" }}
                    >
                        <AvGroup style={{ width: "50%" }}>
                            <StyledLabel className="mt-3" htmlFor="size">
                                {t("Font size")}
                            </StyledLabel>
                            <StyledSelectInput
                                value={{
                                    label: fontSize,
                                    value: fontSize,
                                }}
                                name="font_size"
                                id="label"
                                style={{ width: "50%" }}
                                className={classnames("imp_inp_fontSize", {
                                    readOnly__clz: isConsult,
                                })}
                                options={fontSizeArray}
                                placeholder={t("Write")}
                                onChange={(e: any) => {
                                    setFontSize(e.label);
                                    setIsEdited(true);
                                }}
                                noOptionsMessage={() => t("No options")}
                                maxMenuHeight="90px"
                            />
                        </AvGroup>

                        <AvGroup style={{ width: "50%" }}>
                            <StyledLabel className="mt-3" htmlFor="width">
                                {t("Ticket width")}
                            </StyledLabel>
                            <StyledSelectInput
                                value={{
                                    label: ticketWidth,
                                    value: ticketWidth,
                                }}
                                name="ticket_width"
                                id="ticket_width"
                                options={ticketWidthArray}
                                placeholder={t("Write")}
                                onChange={(e: any) => {
                                    setTicketWidth(e.label);
                                    setIsEdited(true);
                                }}
                                noOptionsMessage={() => t("No options")}
                                maxMenuHeight="90px"
                                className={classnames("imp_inp_ticketWidth", {
                                    readOnly__clz: isConsult,
                                })}
                            />
                        </AvGroup>
                    </div>

                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="mark">
                            {t("Mark")}
                        </StyledLabel>
                        <StyledTextInput
                            style={{ width: "100%" }}
                            id="mark"
                            name="mark"
                            placeholder={t("Write")}
                            type="text"
                            onChange={(e: any) => {
                                setMark(e.target.value);
                                setIsEdited(true);
                            }}
                            value={mark}
                            className={classnames("imp_inp_mark", {
                                readOnly__clz: isConsult,
                            })}
                        />
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("Ticket")}
                        </StyledLabel>
                        <StyledSelectInput
                            onDelete={() => {
                                setIsEdited(true);
                            }}
                            onChange={(e: any) => {
                                setIsEdited(true);
                                setTickets(e);
                                setInputTicket(false);
                            }}
                            className={classnames("imp_drp_ticket", {
                                input__clz: inputTicket,
                                readOnly__clz: isConsult,
                            })}
                            placeholder={`${t("Select")}…`}
                            value={tickets}
                            name="Ticket"
                            isClearable
                            options={ticketsList}
                            noOptionsMessage={() => t("No options")}
                        />
                        {inputTicket ? (
                            <div
                                style={{
                                    width: "100%",
                                    marginTop: "0.25rem",
                                    fontSize: "80%",
                                    color: "#f46a6a",
                                }}
                            >
                                {t("Please select a ticket")}
                            </div>
                        ) : null}
                    </AvGroup>

                    <AvGroup>
                        <div className="d-flex align-items-center justify-content-between mt-3">
                            <StyledLabel className="mb-0" htmlFor="open-drawer">
                                {t("Open cash drawer when printing")}
                            </StyledLabel>
                            <ReactSwitch
                                id="open-drawer"
                                data-testid="peripherals-printer-open-drawer-switch"
                                uncheckedIcon={false}
                                checkedIcon={false}
                                handleDiameter={26}
                                offColor="#f7b4b8"
                                offHandleColor="#E30613"
                                onColor="#c2eddd"
                                onHandleColor="#34C38F"
                                width={55}
                                height={20}
                                checked={openDrawer}
                                disabled={isConsult}
                                onChange={() => {
                                    if (isConsult) return;
                                    setOpenDrawer(!openDrawer);
                                    setIsEdited(true);
                                }}
                            />
                        </div>
                    </AvGroup>

                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="remark">
                            {t("Remark")}
                        </StyledLabel>
                        <StyledTextInput
                            style={{ width: "100%" }}
                            id="remark"
                            name="remark"
                            placeholder={t("Write")}
                            type="text"
                            onChange={(e: any) => {
                                setRemark(e.target.value);
                                setIsEdited(true);
                            }}
                            value={remark}
                            className={classnames("imp_inp_remark", {
                                readOnly__clz: isConsult,
                            })}
                        />
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter className="pt-1 pb-2">
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2 imp_btn_cancel cmn_btn_cancel"
                        variant="light"
                        onClick={() => {
                            if (isEdited === true) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(() => {
                                    setEditedData([]);
                                    setIsAddPrinterClicked!(
                                        !isAddPrinterClicked
                                    );
                                    setIsConsult(false);
                                    setIp("");
                                    activateCheckbox();
                                    setInputIp(false);
                                    setIsEdited(false);
                                });
                            } else {
                                setEditedData([]);
                                setIsAddPrinterClicked!(!isAddPrinterClicked);
                                setIsConsult(false);
                                setIp("");
                                activateCheckbox();
                                setInputIp(false);
                            }
                        }}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        variant="primary"
                        className={classnames(
                            "imp_btn_validate cmn_btn_validate",
                            {
                                "not-allowed-icon__clz": isConsult || isWaiting,
                            }
                        )}
                        onClick={() => {
                            if (designation === "") {
                                setInputDesignation(true);
                            }
                            if (tickets === null) {
                                setInputTicket(true);
                            }
                            if (!ip.match(ipMatchWord)) {
                                setInputIp(true);
                            }
                            if (ip === "") {
                                setEmptyIp(true);
                            }
                            if (
                                printerType === "series" &&
                                !isSeriesPortFilled(portSeries)
                            ) {
                                setInputPortSeries(true);
                            }
                            const isPrinterSavable = canSavePrinter({
                                isIpPrinter,
                                isUsbPrinter,
                                codeIsExist,
                                isCodeValid,
                                printerCode,
                                designation,
                                isNameExist: exist.isNameExist,
                                tickets,
                                ip,
                                isIpExist: exist.isIpExist,
                                ipPortNumber,
                                portSeries,
                                ipMatchWord,
                                numberMatchWord,
                            });
                            if (editedData.length === 0) {
                                if (isPrinterSavable) {
                                    handleValidateButtonClickEvent();
                                }
                            } else {
                                if (isEdited === false) {
                                    setIsModalOpened(true);
                                } else if (isPrinterSavable)
                                    handleEditButtonClickEvent();
                            }
                        }}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </CustomCardFooter>
                <PrintersPathsModal
                    setIsPrintersPathsModalOpened={
                        setIsPrintersPathsModalOpened
                    }
                    isPrintersPathsModalOpened={isPrintersPathsModalOpened}
                    setPrinterPath={setPrinterPath}
                    printerPath={printerPath}
                />
            </AvForm>
            <ConfirmationEditMessage
                setDataIsUpdated={setDataIsUpdated}
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                isAddClicked={isAddPrinterClicked}
                setIsAddClicked={setIsAddPrinterClicked}
                setEditedData={setEditedData}
            />
            {isActionsClicked === true ? (
                <ConfirmationUpdatingDataMessageModal
                    isModalOpened={isActionsClicked}
                    setIsModalOpened={setIsActionsClicked}
                    validationAction={validationAction}
                    actions={actions}
                    setIsEdited={setIsEdited}
                />
            ) : null}
        </React.Fragment>
    );
}
